/**
 * Contexto para gerenciar salvamentos pendentes de itens do pedido
 * 
 * IMPORTANTE: Este provider deve ficar no nível do PainelPedidoProvider,
 * NÃO dentro do Sheet que é desmontado ao fechar.
 * Isso garante que o estado da fila persista entre abrir/fechar do painel.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { usePendingSaves } from '@/hooks/usePendingSaves';

type PendingSavesContextType = ReturnType<typeof usePendingSaves>;

const PendingSavesContext = createContext<PendingSavesContextType | null>(null);

export function PendingSavesProvider({ children }: { children: ReactNode }) {
  const pendingSaves = usePendingSaves();
  
  return (
    <PendingSavesContext.Provider value={pendingSaves}>
      {children}
    </PendingSavesContext.Provider>
  );
}

export function usePendingSavesContext() {
  const ctx = useContext(PendingSavesContext);
  if (!ctx) {
    // Fallback silencioso para componentes fora do provider (não deveria acontecer)
    console.warn('⚠️ [usePendingSavesContext] Contexto não encontrado - usando fallback');
    return {
      enqueue: async () => {},
      hasPending: () => false,
      hasAnyPending: () => false,
      flushAll: async () => ({ success: true, errors: [] }),
      getError: () => null,
      clearError: () => {},
      discardPending: () => {},
      discardAll: () => {},
      isProcessing: () => false,
      getPendingItemIds: () => [],
      _version: 0
    } as PendingSavesContextType;
  }
  return ctx;
}
